import { useState } from 'react';
import { Send, CheckCircle2, Loader2 } from 'lucide-react';
import { useBotShield, HoneypotField, shieldErrorMessage } from '../lib/botShield';

const API = process.env.REACT_APP_BACKEND_URL;

const NewsletterForm = ({ email, setEmail, state, setState, handleSubmit, hp, setHp }) => (
  <form onSubmit={handleSubmit} className="flex items-center gap-2 w-full md:w-auto">
    <HoneypotField value={hp} onChange={setHp} />
    <input
      data-testid="newsletter-email-input"
      type="email"
      required
      value={email}
      onChange={(e) => { setEmail(e.target.value); if (state === 'error') setState('idle'); }}
      placeholder="you@company.com"
      className="flex-1 min-w-0 md:w-[280px] px-4 py-2.5 rounded-lg bg-white/[0.04] border border-white/10 text-white text-sm placeholder:text-[#94A3B8] focus:outline-none focus:border-[#F29B3D] focus:ring-1 focus:ring-[#F29B3D]/40 transition-all"
    />
    <button
      data-testid="newsletter-submit-button"
      type="submit"
      disabled={state === 'loading'}
      className="shrink-0 px-5 py-2.5 rounded-lg bg-[#F29B3D] hover:bg-[#DD8226] text-white font-semibold text-sm transition-colors flex items-center gap-2 disabled:opacity-60"
    >
      {state === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : <Send className="w-4 h-4" strokeWidth={2} />}
      Subscribe
    </button>
  </form>
);

export const NewsletterSignup = () => {
  const [email, setEmail] = useState('');
  const [state, setState] = useState('idle');
  const [message, setMessage] = useState('');
  const { shieldFields, hp, setHp, refresh } = useBotShield();

  const fail = (msg) => {
    setMessage(msg);
    setState('error');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || state === 'loading') return;
    setState('loading');
    try {
      const res = await fetch(`${API}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, ...shieldFields })
      });
      const shieldMsg = shieldErrorMessage(res.status, refresh);
      if (shieldMsg) return fail(shieldMsg);
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      setMessage(data.message);
      setState('success');
    } catch {
      fail('Please enter a valid email address.');
    }
  };

  return (
    <div data-testid="newsletter-signup" className="border-t border-white/[0.06] relative z-10">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
          <div>
            <h4 className="text-white font-semibold text-sm mb-1">Stay ahead of the cloud curve</h4>
            <p className="text-[#93A5BC] text-xs">Product updates, engineering deep-dives, and Africa cloud insights. Monthly, no spam.</p>
          </div>
          {state === 'success' ? (
            <div data-testid="newsletter-success" className="flex items-center gap-2 text-[#16A34A] text-sm">
              <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} />
              {message}
            </div>
          ) : (
            <NewsletterForm email={email} setEmail={setEmail} state={state} setState={setState}
              handleSubmit={handleSubmit} hp={hp} setHp={setHp} />
          )}
        </div>
        {state === 'error' && (
          <p data-testid="newsletter-error" className="text-red-400 text-xs mt-2 md:text-right">{message}</p>
        )}
      </div>
    </div>
  );
};

export default NewsletterSignup;
